import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getBryntumRecordAsObject, isObjDeepEqual } from "../../other/utils/utils.jsx";

const getObjectDifferences = (obj1, obj2) => {
  const differences = {};

  const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

  for (const key of allKeys) {
    const value1 = obj1[key];
    const value2 = obj2[key];

    if (!isObjDeepEqual(value1, value2)) {
      differences[key] = value2;
    }
  }

  return differences;
};

export const useSwivuForm = ({ schema, record, updateRecordOnFormValueChange, version, defaultValues = {} }) => {
  const isUpdatingRef = useRef(false);
  const previousRecordUpdateCount = useRef(version);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { ...getBryntumRecordAsObject(record), ...defaultValues },
    mode: "onChange",
  });

  useEffect(() => {
    if (previousRecordUpdateCount.current !== version && !isUpdatingRef.current) {
      isUpdatingRef.current = true;
      const formValues = form.getValues();
      const differences = getObjectDifferences(formValues, getBryntumRecordAsObject(record));
      for (const key in differences) {
        form.setValue(key, differences[key]);
      }
      isUpdatingRef.current = false;
    }
  }, [version]);

  useEffect(() => {
    if (updateRecordOnFormValueChange) {
      const cleanupSubscription = form.subscribe({
        formState: { values: true },
        callback: async ({ name, values }) => {
          if (!name || isUpdatingRef.current) return;

          const recordFieldName = name.split(".")[0]; // sometimes the name of field is nested like 'taskFilter.0.name' etc, here we only want to access the top level value

          const baseSchema = schema._def.schema;
          const fieldSchema = baseSchema.shape?.[recordFieldName];

          if (!fieldSchema) {
            console.warn(`Field "${name}" not found in schema`);
            return;
          }
          const result = fieldSchema.safeParse(values[recordFieldName]);

          if (result.success) {
            isUpdatingRef.current = true;
            record[recordFieldName] = values[recordFieldName];
            isUpdatingRef.current = false;
          }
        }
      });
      return () => cleanupSubscription();
    }
  }, [form, record, updateRecordOnFormValueChange]);

  return { form, formState: form.formState };
};